<template>
  <tr>
    <td v-if="showReadModels && !readModelMerge.isDisplayed" :rowspan="readModelMerge.rowSpan">
      <domain-model-read-model
        :system="system"
        :event="event"
        :refreshKey="refreshKey"
        :showEntities="showEntities"
      />
    </td>
    <td v-if="role && !role.isDisplayed" :rowspan="role.rowSpan">
      <domain-model-roles :system="system" :role="role" :refreshKey="refreshKey" />
    </td>
    <td>
      <domain-model-write-model
        :system="system"
        :event="event"
        :refreshKey="refreshKey"
        :showEntities="showEntities"
        :currentSystemEntitiesData="currentSystemEntitiesData"
        @refreshArrows="refreshArrows"
      />
    </td>
    <td v-if="event.index === 0" :rowspan="system.events.length">
      <domain-model-aggregate-root
        :system="system"
        :refreshKey="refreshKey"
        :boundedContext="boundedContext"
        :showEntities="showEntities"
        :currentSystemEntitiesData="currentSystemEntitiesData"
        @refreshArrows="refreshArrows"
      />
    </td>
    <td v-if="!showEventDetails">
      <domain-model-resulting-events :system="system" :event="event" :refreshKey="refreshKey" />
    </td>
    <td v-if="showEventDetails && !domainEventCardType">
      <aggregate-type-selector
        :cardTypes="cardTypes"
        :type="DomainModelRole.DOMAIN_EVENT"
      />
    </td>
    <td v-if="showEventDetails && domainEventCardType">
      <domain-model-domain-event
        :system="system"
        :event="event"
        :boundedContext="boundedContext"
        :showEntities="showEntities"
        :refreshKey="refreshKey"
        @refreshArrows="refreshArrows"
      />
    </td>
    <td v-if="showEventDetails && domainEventCardType">
      <domain-model-updated-projection
        :system="system"
        :event="event"
        :showEntities="showEntities"
        :refreshKey="refreshKey"
      />
    </td>
    <td v-if="showEventDetails && !domainEventCardType">
    </td>
  </tr>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import DomainModelRoles from '@/components/DomainModelRoles.vue'
import DomainModelReadModel from '@/components/DomainModelReadModel.vue'
import DomainModelWriteModel from '@/components/DomainModelWriteModel.vue'
import DomainModelAggregateRoot from '@/components/DomainModelAggregateRoot.vue'
import DomainModelResultingEvents from '@/components/DomainModelResultingEvents.vue'
import DomainModelDomainEvent from '@/components/DomainModelDomainEvent.vue'
import DomainModelUpdatedProjection from '@/components/DomainModelUpdatedProjection.vue'
import AggregateTypeSelector from '@/components/AggregateTypeSelector'
import { fieldNameIsEntityIdReference } from '@/utils/fieldNameMatch'
import { cardHelpers } from '@/mixins/cardHelpers'
import { DomainModelRole } from '@/constants/cardTypes'

const {
  system,
  event,
  boundedContext,
  showEntities,
  showReadModels,
  showEventDetails,
  domainEventCardType,
  refreshKey
} = defineProps({
  system: Object,
  event: Object,
  boundedContext: Object,
  refreshKey: Number,
  showEntities: Boolean,
  showReadModels: Boolean,
  showEventDetails: Boolean,
  domainEventCardType: Object
})
const emit = defineEmits(['refreshArrows'])

const store = useStore()

const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const workflow = computed(() => store.state.workflow.workflow)
const role = computed(() => {
  const currentRole = system.roles[event.index]
  const rowSpan = system.roles.filter((item) => item.id === currentRole.id).length
  const isDisplayed = event.index !== 0 && currentRole.id === system.roles[event.index - 1].id

  return { ...currentRole, index: event.index, rowSpan, isDisplayed }
})

const getQuerySignature = (eventIndex) => {
  const queries = system.queries[eventIndex] || []
  const schemaIds = queries.map((q) => q.schemaId).filter(Boolean).sort().join(',')
  return schemaIds
}

const readModelMerge = computed(() => {
  if (!showReadModels) return { rowSpan: 1, isDisplayed: false }

  const currentSignature = getQuerySignature(event.index)
  const currentRoleId = system.roles[event.index]?.id

  if (!currentSignature) return { rowSpan: 1, isDisplayed: false }

  const isDisplayed = event.index > 0 && (() => {
    const prevSignature = getQuerySignature(event.index - 1)
    const prevRoleId = system.roles[event.index - 1]?.id
    return currentSignature === prevSignature && currentRoleId === prevRoleId
  })()

  let rowSpan = 1
  if (!isDisplayed) {
    for (let i = event.index + 1; i < system.events.length; i++) {
      const nextSignature = getQuerySignature(i)
      const nextRoleId = system.roles[i]?.id
      if (nextSignature === currentSignature && nextRoleId === currentRoleId) {
        rowSpan++
      } else {
        break
      }
    }
  }

  return { rowSpan, isDisplayed }
})

const currentSystemEntitiesData = computed(() => {
  const entities = []
  const schemas = workflow.value.schemas || []
  const rootEntityNames = []
  const rootEntity = system.groupedBy?.type === 'linked-entity' ? system.groupedBy.rootEntity : null

  for (const schema of schemas) {
    for (const event of system.events) {
      const entityCard = cardHelpers.methods.getEntityCard(event)
      const commandCard = cardHelpers.methods.getCommandCard(event)
      const commandSchema = commandCard?.schemaId
        ? schemas.find((s) => s.id === commandCard.schemaId)
        : null
      // Explanation of: field.fields?.length > 0
      // We only show entities that are truly part of the aggregate.
      // If an entity is referenced by ID only (i.e., no subfields in the command),
      // we treat it as external to the aggregate.
      // Therefore, related entities without any sub data fields in the command are excluded.
      const isRelatedEntity = commandSchema?.fields?.some((field) => field.relatedEntityId === schema.id && field.fields?.length > 0)

      const isIdReferencedInAggregate = !isRelatedEntity &&
        rootEntity &&
        schema.id !== rootEntity.id &&
        commandSchema?.fields?.some((field) => fieldNameIsEntityIdReference(field.name, schema.name)) &&
        rootEntity.fields?.some((field) => field.relatedEntityId === schema.id)

      if (isRelatedEntity || isIdReferencedInAggregate) {
        if (entityCard?.schemaId === schema.id) {
          rootEntityNames.push(schema.name)
        }

        if (entities.every((entity) => entity.name !== schema.name)) {
          entities.push(schema)
        }
      }
    }
  }

  const matchingRootEntity = system.groupedBy?.type === 'linked-entity'
    ? system.groupedBy.rootEntity
    : entities.find((entity) => entity.name === system.groupedBy?.text)

  return {
    entities: matchingRootEntity
      ? entities.filter((entity) => !rootEntityNames.includes(entity.name))
      : entities.map((entity) => rootEntityNames.includes(entity.name) ? { ...entity, isRoot: true } : entity),
    rootEntity: matchingRootEntity ? { ...matchingRootEntity, name: matchingRootEntity.name } : null
  }
})

const refreshArrows = () => {
  emit('refreshArrows')
}
</script>

<style scoped>
tr {
  td {
    padding: 10px 16px;
    position: static;

    &:focus-within, &:focus {
      outline: none;
    }
  }
}
</style>
